import * as THREE from "three";

const CARD_WIDTH = 3.05;
const CARD_HEIGHT = 4.27;
const CORNER = 0.2;
const FACE_OFFSET = 0.05;

export const SHARE_CARD_LIGHTS = {
  ambient: 0x24383a,
  ambientIntensity: 0.32,
  key: 0xe8c36a,
  keyIntensity: 0.62,
  fill: 0x3db8b6,
  fillIntensity: 0.32,
  rim: 0xd4a017,
  rimIntensity: 0.22,
};

export function addShareCardLights(scene, Three = THREE) {
  scene.add(new Three.AmbientLight(SHARE_CARD_LIGHTS.ambient, SHARE_CARD_LIGHTS.ambientIntensity));
  const key = new Three.DirectionalLight(SHARE_CARD_LIGHTS.key, SHARE_CARD_LIGHTS.keyIntensity);
  key.position.set(3, 5, 6);
  scene.add(key);
  const fill = new Three.PointLight(SHARE_CARD_LIGHTS.fill, SHARE_CARD_LIGHTS.fillIntensity, 70);
  fill.position.set(-5, 1, 4);
  scene.add(fill);
  const rim = new Three.PointLight(SHARE_CARD_LIGHTS.rim, SHARE_CARD_LIGHTS.rimIntensity, 70);
  rim.position.set(5, -2, 3);
  scene.add(rim);
}

export function hexToColor(hex) {
  return Number.parseInt(String(hex || "#84E6E5").replace("#", ""), 16);
}

export function roundedCardShape(width, height, radius, Shape = THREE.Shape) {
  const shape = new Shape();
  const x = -width / 2;
  const y = -height / 2;
  shape.moveTo(x + radius, y);
  shape.lineTo(x + width - radius, y);
  shape.quadraticCurveTo(x + width, y, x + width, y + radius);
  shape.lineTo(x + width, y + height - radius);
  shape.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  shape.lineTo(x + radius, y + height);
  shape.quadraticCurveTo(x, y + height, x, y + height - radius);
  shape.lineTo(x, y + radius);
  shape.quadraticCurveTo(x, y, x + radius, y);
  return shape;
}

export function remapCardUVs(geometry, width, height) {
  const positions = geometry.attributes.position;
  const uvs = geometry.attributes.uv;
  for (let i = 0; i < positions.count; i += 1) {
    uvs.setXY(i, (positions.getX(i) + width / 2) / width, (positions.getY(i) + height / 2) / height);
  }
  uvs.needsUpdate = true;
}

function createEnvTexture() {
  const canvas = document.createElement("canvas");
  canvas.width = 1024;
  canvas.height = 512;
  const ctx = canvas.getContext("2d");
  const gradient = ctx.createLinearGradient(0, 0, 0, 512);
  gradient.addColorStop(0, "#3a3f49");
  gradient.addColorStop(0.42, "#0c0e12");
  gradient.addColorStop(0.5, "#20242c");
  gradient.addColorStop(0.58, "#0b0d10");
  gradient.addColorStop(1, "#2b2f37");
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 1024, 512);

  const blob = (x, y, radius, alpha) => {
    const radial = ctx.createRadialGradient(x, y, 0, x, y, radius);
    radial.addColorStop(0, `rgba(232,195,106,${alpha})`);
    radial.addColorStop(1, "rgba(232,195,106,0)");
    ctx.fillStyle = radial;
    ctx.fillRect(x - radius, y - radius, radius * 2, radius * 2);
  };
  blob(230, 150, 120, 0.38);
  blob(760, 120, 150, 0.28);
  blob(520, 380, 180, 0.18);

  const texture = new THREE.CanvasTexture(canvas);
  texture.mapping = THREE.EquirectangularReflectionMapping;
  return texture;
}

function makeFaceTexture(canvas, { flipX = false } = {}) {
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 8;
  texture.needsUpdate = true;
  if (flipX) {
    texture.wrapS = THREE.RepeatWrapping;
    texture.repeat.x = -1;
    texture.offset.x = 1;
  }
  return texture;
}

export function mountShareCard3D({
  mount,
  frontCanvas,
  backCanvas,
  edge = "#3db8b6",
  reduceMotion = false,
}) {
  if (!mount) return { dispose() {} };
  mount.setAttribute("aria-label", "Trivia share card preview");
  mount.replaceChildren();

  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  } catch {
    return { dispose() {} };
  }

  const width = mount.clientWidth || 300;
  const height = mount.clientHeight || 420;
  renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
  renderer.setSize(width, height);
  renderer.domElement.style.touchAction = "none";
  renderer.domElement.style.cursor = "grab";
  renderer.domElement.style.display = "block";
  renderer.domElement.style.width = "100%";
  renderer.domElement.style.height = "100%";
  mount.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(32, width / height, 0.1, 100);
  camera.position.set(0, 0, 8);

  const envMap = createEnvTexture();
  addShareCardLights(scene);

  const frontMap = makeFaceTexture(frontCanvas);
  const backMap = makeFaceTexture(backCanvas, { flipX: true });
  const frontMat = new THREE.MeshBasicMaterial({ map: frontMap });
  const backMat = new THREE.MeshBasicMaterial({ map: backMap });
  const edgeMat = new THREE.MeshStandardMaterial({
    color: hexToColor(edge),
    metalness: 1,
    roughness: 0.22,
    envMap,
    envMapIntensity: 0.95,
  });

  const shape = roundedCardShape(CARD_WIDTH, CARD_HEIGHT, CORNER);
  const frontGeo = new THREE.ShapeGeometry(shape);
  remapCardUVs(frontGeo, CARD_WIDTH, CARD_HEIGHT);
  const backGeo = new THREE.ShapeGeometry(shape);
  remapCardUVs(backGeo, CARD_WIDTH, CARD_HEIGHT);
  const frontMesh = new THREE.Mesh(frontGeo, frontMat);
  frontMesh.position.z = FACE_OFFSET;
  const backMesh = new THREE.Mesh(backGeo, backMat);
  backMesh.position.z = -FACE_OFFSET;
  backMesh.rotation.y = Math.PI;
  const rimGeo = new THREE.ExtrudeGeometry(shape, { depth: 0.09, bevelEnabled: false });
  rimGeo.translate(0, 0, -0.045);
  const rim = new THREE.Mesh(rimGeo, edgeMat);
  const card = new THREE.Group();
  card.add(rim, frontMesh, backMesh);
  scene.add(card);

  let targetX = 0.06;
  let targetY = 0.16;
  let currentX = 0.06;
  let currentY = 0.16;
  let velocityX = 0;
  let velocityY = 0;
  let dragging = false;
  let lastX = 0;
  let lastY = 0;
  let idle = 0;
  let frame = 0;

  const pointer = (event) => {
    const source = event.touches ? event.touches[0] : event;
    return { x: source.clientX, y: source.clientY };
  };

  const onDown = (event) => {
    dragging = true;
    idle = 0;
    const point = pointer(event);
    lastX = point.x;
    lastY = point.y;
    renderer.domElement.style.cursor = "grabbing";
  };
  const onMove = (event) => {
    if (!dragging) return;
    const point = pointer(event);
    velocityY = (point.x - lastX) * 0.01;
    velocityX = (point.y - lastY) * 0.01;
    targetY += velocityY;
    targetX = Math.max(-0.5, Math.min(0.5, targetX + velocityX));
    lastX = point.x;
    lastY = point.y;
  };
  const onUp = () => {
    dragging = false;
    renderer.domElement.style.cursor = "grab";
  };
  const onResize = () => {
    const nextWidth = mount.clientWidth || width;
    const nextHeight = mount.clientHeight || height;
    renderer.setSize(nextWidth, nextHeight);
    camera.aspect = nextWidth / nextHeight;
    camera.updateProjectionMatrix();
  };

  renderer.domElement.addEventListener("pointerdown", onDown);
  window.addEventListener("pointermove", onMove);
  window.addEventListener("pointerup", onUp);
  renderer.domElement.addEventListener("touchstart", onDown, { passive: true });
  renderer.domElement.addEventListener("touchmove", onMove, { passive: true });
  renderer.domElement.addEventListener("touchend", onUp);
  window.addEventListener("resize", onResize);

  const tick = () => {
    frame = requestAnimationFrame(tick);
    if (!dragging) {
      idle += 1;
      if (!reduceMotion && idle > 40) targetY += 0.002;
      targetY += velocityY;
      targetX = Math.max(-0.5, Math.min(0.5, targetX + velocityX));
      velocityY *= 0.92;
      velocityX *= 0.92;
    }
    currentY += (targetY - currentY) * 0.12;
    currentX += (targetX - currentX) * 0.12;
    card.rotation.y = currentY;
    card.rotation.x = currentX;
    renderer.render(scene, camera);
  };
  tick();

  return {
    dispose() {
      cancelAnimationFrame(frame);
      renderer.domElement.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      renderer.domElement.removeEventListener("touchstart", onDown);
      renderer.domElement.removeEventListener("touchmove", onMove);
      renderer.domElement.removeEventListener("touchend", onUp);
      window.removeEventListener("resize", onResize);
      frontMap.dispose();
      backMap.dispose();
      frontGeo.dispose();
      backGeo.dispose();
      rimGeo.dispose();
      envMap.dispose();
      renderer.dispose();
      mount.replaceChildren();
    },
  };
}
